import { Box, Button, Typography } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import Alert from './Alert';
import '../template.module.css';
import { ReportTemplatesContext } from '..';

export default function AddMapsToTemplateSection({ isOpen = true, onClose }) {
  const { Maps, getAccessToken, fetchMaps } = useContext(ReportTemplatesContext);
  const [searchResults, setSearchResults] = useState(Maps || []);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimer = useRef(null);

  useEffect(() => {
    const dialog = document.getElementById('add-maps-modal');
    if (isOpen) {
      dialog.showModal();
      if (fetchMaps) {
        fetchMaps();
      }
    } else {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (Maps && Maps.length > 0 && !searchQuery) {
      setSearchResults(Maps);
    }
  }, [Maps, searchQuery]);

  async function searchMaps(query) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/map/search?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        },
      );
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return data.status ? data.maps : [];
    } catch (error) {
      console.error('Error searching maps:', error);
      return [];
    }
  }

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      setSearchResults(
        !searchQuery
          ? Maps
          : (await searchMaps(searchQuery)).map(mapID =>
              Maps.find(m => m._id === mapID._id),
            ),
      );
    }, 500);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [searchQuery, Maps]);

  const handleSearch = e => setSearchQuery(e.target.value);

  const groupedMaps = (searchResults || []).reduce((acc, map) => {
    if (!map) return acc;
    const tags = map.tags || [];
    if (tags.length === 0) {
      if (!acc['Uncategorized']) acc['Uncategorized'] = [];
      acc['Uncategorized'].push(map);
    } else {
      tags.forEach(tag => {
        const normalizedTag =
          tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
        if (!acc[normalizedTag]) acc[normalizedTag] = [];
        acc[normalizedTag].push(map);
      });
    }
    return acc;
  }, {});

  return (
    <dialog id='add-maps-modal'>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Heading and Button to Add Maps */}
        <Box
          sx={{
            paddingX: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 800,
              color: '#1A202C',
            }}
          >
            Add Maps to your Template
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              alignSelf: 'center',
              backgroundColor: '#4FA490',
              color: '#FFFFFF',
              padding: '0.5rem 1.25rem',
              borderRadius: '0.75rem',
              '&:hover': {
                backgroundColor: '#3A8B7A',
              },
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.2px',
              textAlign: 'center',
            }}
          >
            Close
          </Button>
        </Box>

        {/* Search Box and Drop Down */}
        <Box
          sx={{
            paddingX: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          {/* Search Box */}
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '1rem',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='lucide lucide-search'
              >
                <circle cx='11' cy='11' r='8' />
                <path d='m21 21-4.3-4.3' />
              </svg>
            </span>
            <input
              placeholder='Search for Maps & Data'
              style={{
                width: '56rem',
                height: '3rem',
                border: '1px solid #DFDFDF',
                borderRadius: '0.75rem',
                paddingLeft: '3.5rem', // adjust this to prevent text from overlapping the icon

                fontFamily: 'Lato',
                fontSize: '0.85rem',
                fontWeight: 800,
                lineHeight: '21px',
                letterSpacing: '0.2px',
                textAlign: 'left',
                color: '#212529',

                '::placeholder': {
                  color: '#212529',
                },
              }}
              onChange={handleSearch}
            />
          </Box>
          <select
            style={{
              width: '100%',
              height: '3.3rem',
              border: '1px solid #DFDFDF',
              borderRadius: '0.75rem',
              paddingLeft: '1rem', // adjust this to prevent text from overlapping the icon
              backgroundColor: '#FFFFFF',
              appearance: 'none',

              fontFamily: 'Lato',
              fontSize: '0.85rem',
              fontWeight: 800,
              lineHeight: '21px',
              letterSpacing: '0.2px',
              textAlign: 'left',
              color: '#212529',
            }}
            onChange={e => {
              if (!e.target.value) {
                setSearchResults(Maps || []);
              } else {
                setSearchResults(
                  (Maps || []).filter(map => 
                    (map.tags || []).includes(e.target.value)
                  ),
                );
              }
            }}
          >
            <option value=''>Category</option>
            {[...new Set(Maps.flatMap(map => map.tags || []))]
              .filter(tag => tag)
              .map(tag => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
          </select>
        </Box>

        {/* Main Content - Maps */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            border: '1px solid #DFDFDF',
            padding: '1rem',
          }}
        >
          {Object.keys(groupedMaps).map((category, index) => (
            <Box key={index}>
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#1A202C',
                }}
              >
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {groupedMaps[category].map((map, mapIndex) => (
                  <MapBox
                    key={mapIndex}
                    sectionTitle={map.title}
                    mapImage={map.imageURL}
                    mapDescription={map.description}
                    sectionKey={map.key}
                  />
                ))}
              </Box>
            </Box>
          ))}
          {searchResults.length === 0 && (
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 700,
                textAlign: 'center',
                color: '#1A202C',
              }}
            >
              No results found
            </Typography>
          )}
        </Box>
      </Box>
    </dialog>
  );
}

function MapBox({ sectionKey, mapImage, sectionTitle, mapDescription }) {
  const { Sections, setSections, Maps } = useContext(ReportTemplatesContext);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  function handleSectionAddition(section) {
    console.log({ chosenSection: section });

    const newSection = {
      ...section,
      key: generateUniqueKey(section.key),
      status: true,
      blocks: [],
    };

    delete newSection.mapBlock;

    if (newSection.map) {
      newSection.map = {
        ...newSection.map,
        status: true,
      };
    }

    setSections(prevSections => [...prevSections, newSection]);
    setAlertMessage(
      `You have added ${section.title} as a new section to your template.`,
    );
    setShowAlert(true);
  }

  function generateUniqueKey(baseKey) {
    const existingKeys = Sections.map(s => s.key);
    let newKey = baseKey;
    let counter = 1;

    while (existingKeys.includes(newKey)) {
      newKey = `${counter}_${baseKey}`;
      counter++;
    }

    return newKey;
  }

  function handleMapClick() {
    const selectedMap = Maps.find(map => map.key === sectionKey);
    const selectedSection = Sections.find(
      section => section.key === sectionKey,
    );
    handleSectionAddition({
      key: sectionKey,
      title: sectionTitle,
      map: {
        ...selectedMap,
        status: true,
      },
      blocks: selectedSection.blocks.map(block => ({
        ...block,
        status: false,
      })),
    });
  }

  return (
    <>
      <Box
        key={sectionKey}
        sx={{
          alignSelf: 'center',
          '@media (max-width: 600px)': {
            width: '100%',
          },
          '@media only screen and (min-width: 600px) and (max-width: 1023px)': {
            width: '49%',
          },
          '@media only screen and (min-width: 1024px)': {
            width: '32%',
          },
          borderRadius: '1.21144rem',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          marginY: '1rem',
          cursor: 'pointer',
          boxShadow: '5px 20px 30px 0px rgba(229, 229, 229,0.7)',
        }}
        onClick={handleMapClick}
      >
        {/* Image */}
        <Box
          sx={{
            width: '100%',
            height: '150px',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {mapImage ? (
            <img
              src={mapImage || ''}
              alt=''
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'cover',
                transform: 'scale(1.8)',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '200px', // You can adjust the height as needed
                backgroundColor: 'gray',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: '16px',
              }}
            >
              No Map Available
            </div>
          )}
        </Box>

        {/* Title and Description */}
        <Box
          sx={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {/* Title */}
          <Typography
            variant='h6'
            component='h3'
            textAlign={'center'}
            sx={{
              fontFamily: '"Lato", sans-serif',
              fontSize: '1.25rem',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: '140%',
              textAlign: 'left',
              color: '#323142',
            }}
          >
            {sectionTitle}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 500,
              lineHeight: '20px',
              textAlign: 'left',
            }}
          >
            {mapDescription}
          </Typography>
        </Box>
      </Box>

      {showAlert && (
        <Alert
          title='Added Map'
          message={alertMessage}
          onClose={() => setShowAlert(false)}
        />
      )}
    </>
  );
}
